import { Fragment } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';

/**
 * What the answer was, and why (SPEC-007 PR-7.4).
 *
 * It reads in one order and it is the order a person asks the questions in: was
 * I right, by how much, and why. WHAT IT NO LONGER DOES IS CARRY THE COMPARISON
 * ITSELF (SPEC-007 D-66): the gauge moved to the band above the pair, where the
 * question was asked, so the two halves of a round are the same composition and
 * the answer arrives in the place the reader was already looking. This band is
 * what is left once that has gone — the outcome, and the two supporting figures
 * — and it is a fraction of the height it was, which is the whole reason the
 * cards no longer have to collapse to pay for it.
 *
 * The per-question SENTENCE is the piece that was cut first, and it is worth
 * saying why rather than leaving a gap somebody helpfully fills back in. With
 * both figures and the gap between them drawn on one scale, "the 512 TR runs to
 * 314 km/h, 44 km/h clear of the 993" is the third telling of the same fact
 * (D-23).
 *
 * WHAT IS LEFT ON SCREEN IS ONE LINE — what the streak cost, and only on an
 * ending. Everything else this band used to draw has moved or gone: the gauge is
 * above the pair (D-66), the outcome is shouted over it (D-60) and said here for
 * a screen reader (D-69), and the two supporting figures went with the fun fact
 * and the link before them (D-70). A correct round now leaves this band with
 * nothing visible in it at all, which is right: the answer is where the question
 * was, and a reader mid-run is looking at the two cards and the button under
 * them.
 *
 * THE DEVICE'S BOARD LEFT LAST, and it is the one departure that is about the
 * desktop rather than about this band. It was rendered here because the two were
 * always on screen together — an ending, at the foot of a phone. A wide screen
 * gives the boards a rail of their own, where the device's five best are up for
 * the WHOLE run rather than only after it, and a panel that is on screen while
 * this band does not exist cannot be a child of it. `DuelScreen` renders it
 * beside this one now; the standing sentence carries its own `role="status"`,
 * which is what it was borrowing from this region.
 *
 * It stays mounted regardless, because it is the LIVE REGION. Everything a
 * screen reader is told about a round is in here, in the order a person asks
 * for it — what happened, then what the numbers were.
 *
 * THE COMPARISON IS STILL SAID HERE, IN TEXT. The drawing above the pair is
 * `aria-hidden`, because a live region up there would talk over the outcome and
 * because a bar is not a thing to read out; so the figures it draws are carried
 * in this region as a clipped description list, announced in the order a reader
 * asks for them — right or wrong, then the numbers, then the gap. That is the
 * same split `DuelQuestion` makes between the question it draws and the question
 * it is: only the pixels are somewhere else.
 *
 * A live region, because a tap on a card is not a navigation and announces
 * nothing on its own. The whole point of the round arrives in here.
 */
export default function DuelReveal({ verdict, insight, run }) {
    const t = useTranslate();
    const isOver = run.status === 'over';
    // Three endings, not two. A round the clock ran out on has no chosen card,
    // and telling the reader they were wrong when they never answered would be
    // the screen misreading its own state back at them.
    const outcome = outcomeOf(verdict);
    const [left] = insight.comparison;

    return (
        <section
            className={`duel-reveal${isOver ? ' duel-reveal--over' : ''}`}
            role="status"
            aria-live="polite"
        >
            {/* THE OUTCOME IS ANNOUNCED AND NOT DRAWN (D-69). The hero over the
                pair says this word at forty times the size and half a second
                earlier, so a second copy down here was the same fact twice —
                and it was the quieter of the two, which is the wrong way round
                for the thing the round was about. It is `aria-hidden` up there,
                though, and this band is the live region, so the word stays in
                the accessibility tree: what left is the pixels.

                Everything in this block is the same trade. The gauge above the
                pair is a drawing; this is it said, and it is the one thing on
                this screen a reader who cannot see the figures must still be
                given. */}
            <div className="visually-hidden">
                <p>{t(`duel.${outcome}`)}</p>

                <p>{t(left.labelKey)}</p>

                <dl>
                    {insight.comparison.map((entry) => (
                        <Fragment key={entry.carId}>
                            <dt>{entry.name}</dt>
                            <dd>
                                {entry.value}
                                {entry.unitKey ? ` ${t(entry.unitKey)}` : ''}
                            </dd>
                        </Fragment>
                    ))}
                </dl>

                <p>
                    {t('duel.gap', {
                        margin: insight.margin,
                        unit: insight.hasUnit ? t(insight.unitKey) : '',
                    })}
                </p>
            </div>

            {/* What the streak cost, and only on an ending. A streak of one and a
                streak of nine end the same way and do not feel the same, which
                is a thing the LOST hero cannot say and this line can. */}
            {outcome === 'correct' ? null : (
                <p className="duel-reveal__ended">
                    {run.streak > 0
                        ? t('duel.streakEnded', { count: run.streak })
                        : t('duel.streakEndedNone')}
                </p>
            )}
        </section>
    );
}

/** `correct`, `wrong`, or `timeUp` — the three ways a round can close. */
function outcomeOf(verdict) {
    if (verdict.isCorrect) {
        return 'correct';
    }

    return verdict.chosen ? 'wrong' : 'timeUp';
}
